'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { AppWindow, ExternalLink } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { useContractCallSection } from '~/app/connect/hooks/useContractCallSection'
import { ImageWithFallbackComponent } from '~/components/ImageWithFallback'
import { SplitsLink } from '~/components/Link'
import {
  Button,
  LoadingSpinner,
  Message,
  NetworkLogo,
  Tooltip,
} from '~/components/component-library'
import {
  Dialog,
  DialogTrigger,
  SplitsDialogContent,
} from '~/components/component-library/Dialog'
import { TransactionMemoInput } from '~/components/inputs/custom/TransactionMemoInput'
import {
  MessageDetailsPreview,
  MessageHeaderPreview,
} from '~/components/smart-accounts/MessageDialog'
import { TransactionDetailsPreview } from '~/components/smart-accounts/TransactionDetailsPreview'
import {
  WalletConnectFormValues,
  WalletConnectPairingDialog,
} from '~/components/smart-accounts/WalletConnectPairingDialog'
import { PendingStateChangeDisplay } from '~/components/txn-display/state-change/components/PendingStateChangeDisplay'
import { useWalletConnectContext } from '~/context/WalletConnectProvider'
import { useActiveOrg, useActiveOrgId } from '~/hooks/org'
import { CreateTransactionData } from '~/hooks/smart-account-actions/createTransactionData.type'
import { useApproveAndSubmitTransaction } from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import {
  CreateMessageData,
  useApproveMessage,
  useCreateMessage,
} from '~/hooks/smart-accounts/useMessageActions'
import { useWalletConnectChainSupport } from '~/hooks/smart-accounts/useWalletConnectChainSupport'
import {
  buildAddToBatchDropdownOptions,
  useAddCurrentFormToBatch,
} from '~/hooks/useAddCurrentFormToBatch'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useOrgSmartAccount } from '~/hooks/useSmartAccounts'
import { useCanAddToBatch } from '~/hooks/useTransactionBatch'
import {
  useCanCreateTransaction,
  useCanExecuteNewTransaction,
} from '~/hooks/useTransactions'
import { WalletConnectControls } from '~/hooks/useWalletConnect'
import {
  BasicTransactionType,
  IAddress,
  OrgSmartAccount,
  OrgTransaction,
} from '~/types'
import { getDomainFromUrl } from '~/utils'
import { Address, EvmAddress } from '~/utils/address'
import { captureMessage } from '~/utils/errors'
import {
  extractAccountFromNamespace,
  extractNamespaceChains,
} from '~/utils/smartAccounts'
import { cn } from '~/utils/tailwind-utils'

export const WalletConnectIcon = ({ className }: { className?: string }) => (
  <svg
    fill="none"
    viewBox="0 0 480 332"
    xmlns="http://www.w3.org/2000/svg"
    className={cn('fill-gray-900 dark:fill-white', className)}
  >
    <path d="m126.613 93.9842c62.622-61.3123 164.152-61.3123 226.775 0l7.536 7.3788c3.131 3.066 3.131 8.036 0 11.102l-25.781 25.242c-1.566 1.533-4.104 1.533-5.67 0l-10.371-10.154c-43.687-42.7734-114.517-42.7734-158.204 0l-11.107 10.874c-1.565 1.533-4.103 1.533-5.669 0l-25.781-25.242c-3.132-3.066-3.132-8.036 0-11.102zm280.093 52.2038 22.946 22.465c3.131 3.066 3.131 8.036 0 11.102l-103.463 101.301c-3.131 3.065-8.208 3.065-11.339 0l-73.432-71.896c-.783-.767-2.052-.767-2.835 0l-73.43 71.896c-3.131 3.065-8.208 3.065-11.339 0l-103.4657-101.302c-3.1311-3.066-3.1311-8.036 0-11.102l22.9456-22.466c3.1311-3.065 8.2077-3.065 11.3388 0l73.4333 71.897c.782.767 2.051.767 2.834 0l73.429-71.897c3.131-3.065 8.208-3.065 11.339 0l73.433 71.897c.783.767 2.052.767 2.835 0l73.431-71.895c3.132-3.066 8.208-3.066 11.339 0z" />
  </svg>
)

export const WalletConnectSection = ({
  address,
  connectOpen,
  onConnectOpenChange,
}: {
  address: IAddress
  /** Pairing-dialog open state, owned by the caller (opened from the actions menu) */
  connectOpen: boolean
  onConnectOpenChange: (open: boolean) => void
}) => {
  const { smartAccount, isLoading } = useOrgSmartAccount(address)

  const { activeSessions } = useWalletConnectContext()
  const canCreateTransaction = useCanCreateTransaction(smartAccount)

  const currentSessions = activeSessions.filter((session) => {
    const address = extractAccountFromNamespace(session.namespaces)
    if (!address) return false

    return Address.equal(address, smartAccount?.address, 'evm')
  })

  if (!canCreateTransaction && !isLoading) return null

  // Render the chips row only when there are sessions, so the trigger-less
  // section doesn't leave an empty flex slot (and gap) in the header.
  return (
    <>
      {currentSessions.length > 0 && (
        <div className="flex items-stretch space-x-2">
          {currentSessions.map((session, index) => (
            <WalletConnectSession
              key={`wallet-connect-session-${index}`}
              session={session}
            />
          ))}
        </div>
      )}
      <WalletConnectConnect
        address={address}
        open={connectOpen}
        onOpenChange={onConnectOpenChange}
      />
    </>
  )
}

export const WalletConnectAppIcon = ({
  appIcon = '',
  appName = 'wallet connect app icon',
  size,
}: {
  appIcon?: string
  appName?: string
  size: number
}) => {
  return (
    <ImageWithFallbackComponent
      src={appIcon}
      alt={appName}
      width={size}
      height={size}
      className="flex-shrink-0 rounded-lg"
      fallbackComponent={
        <div
          style={{ width: size, height: size }}
          className="relative flex flex-col items-center justify-center rounded-lg bg-gray-100 transition dark:bg-gray-800"
        >
          <AppWindow className="h-5 w-5" />
          <div className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full bg-green-500 shadow" />
        </div>
      }
    />
  )
}

const WalletConnectSession = ({
  session,
}: {
  session: WalletConnectControls['activeSessions'][0]
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const { disconnectSession } = useWalletConnectContext()

  const appName = session.peer.metadata.name ?? session.peer.metadata.url
  const appIcon = session.peer.metadata.icons?.[0]
  const supportedChainIds = extractNamespaceChains(session.namespaces)
  // once you're already connected, all chains are effectively "required"
  const requiredChainIds = supportedChainIds
  const optionalChainIds: number[] = []

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip
        position="bottom"
        content={
          <>
            Connected to {appName} on {supportedChainIds.length} networks
          </>
        }
      >
        <DialogTrigger asChild>
          <Button variant="secondary" size="small">
            <WalletConnectAppIcon
              appIcon={appIcon}
              appName={appName}
              size={20}
            />
          </Button>
        </DialogTrigger>
      </Tooltip>
      <SplitsDialogContent
        id="walletConnectSession"
        title="Connected via WalletConnect"
        variant="destructive"
        deleteButton={{
          text: 'Disconnect',
          onClick: async () => {
            await disconnectSession(session.topic)
            setIsOpen(false)
          },
        }}
      >
        <WalletConnectConnectionContent
          metadata={session.peer.metadata}
          isDanger={false}
          isVerified={false}
          supportedChainIds={supportedChainIds}
          requiredChainIds={requiredChainIds}
          optionalChainIds={optionalChainIds}
        />
      </SplitsDialogContent>
    </Dialog>
  )
}

const WalletConnectConnect = ({
  address,
  open,
  onOpenChange,
}: {
  address: IAddress
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const { smartAccount } = useOrgSmartAccount(address)

  const { user: authUser } = useAuthUser()
  const activeOrgId = useActiveOrgId()
  const canUseWalletConnect = authUser && smartAccount?.orgId === activeOrgId
  const { control, handleSubmit, reset } = useForm<WalletConnectFormValues>({
    defaultValues: {
      uri: '',
    },
  })

  if (!canUseWalletConnect) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <WalletConnectPairingDialog
          control={control}
          handleSubmit={handleSubmit}
          open={open}
        />
      </Dialog>
      <WalletConnectConnection
        address={address}
        resetForm={reset}
        closeUriModal={() => onOpenChange(false)}
      />
    </>
  )
}

const WalletConnectConnection = ({
  address,
  resetForm,
  closeUriModal,
}: {
  address: IAddress
  resetForm: () => void
  closeUriModal: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { smartAccount, isLoading: isLoadingSmartAccount } =
    useOrgSmartAccount(address)
  const { org: activeOrg } = useActiveOrg()
  const { pendingProposal, isApprovingSession, approveSession, rejectSession } =
    useWalletConnectContext()

  useEffect(() => {
    if (!isOpen) resetForm()
  }, [isOpen, resetForm])

  useEffect(() => {
    if (pendingProposal) {
      setIsOpen(true)
      closeUriModal()
    }
  }, [pendingProposal, closeUriModal])

  const isVerified =
    pendingProposal?.verifyContext.verified.validation === 'VALID'
  const isDanger =
    pendingProposal?.verifyContext.verified.isScam ||
    pendingProposal?.verifyContext.verified.validation === 'INVALID'

  const requiredChainIds = extractNamespaceChains(
    pendingProposal?.params.requiredNamespaces,
  )
  const optionalChainIds = extractNamespaceChains(
    pendingProposal?.params.optionalNamespaces,
  )

  const {
    filteredSupportedChainIds,
    appSupportsCurrentChains,
    hasUnsupportedByTeamsChains,
    hasNotEnabledOnOrgChains,
    unsupportedByTeamsChainIds,
    notEnabledOnOrgChainIds,
  } = useWalletConnectChainSupport({
    requiredChainIds,
    optionalChainIds,
    supportedChainIds: activeOrg?.smartAccountsChainIds || [],
  })

  const isDisabled =
    !appSupportsCurrentChains || isLoadingSmartAccount || !smartAccount

  // Log chain incompatibility for debugging and monitoring
  const loggedProposalIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (
      pendingProposal &&
      !appSupportsCurrentChains &&
      loggedProposalIdRef.current !== pendingProposal.id
    ) {
      loggedProposalIdRef.current = pendingProposal.id
      const appName = pendingProposal.params.proposer.metadata.name
      const appUrl = pendingProposal.params.proposer.metadata.url
      captureMessage('WalletConnect chain incompatibility', {
        tags: {
          feature: 'walletconnect',
        },
        extra: {
          appName,
          appUrl,
          requiredChainIds,
          optionalChainIds,
          orgEnabledChainIds: activeOrg?.smartAccountsChainIds || [],
          unsupportedByTeamsChainIds,
          notEnabledOnOrgChainIds,
        },
      })
    }
  }, [
    pendingProposal,
    appSupportsCurrentChains,
    requiredChainIds,
    optionalChainIds,
    activeOrg?.smartAccountsChainIds,
    unsupportedByTeamsChainIds,
    notEnabledOnOrgChainIds,
  ])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={async (newIsOpen: boolean) => {
        if (!newIsOpen && pendingProposal) {
          await rejectSession(pendingProposal)
        }
        setIsOpen(newIsOpen)
      }}
    >
      {!pendingProposal ? (
        <SplitsDialogContent
          id="walletConnectLoading"
          title="Connecting via WalletConnect"
          variant="info"
        >
          <LoadingSpinner size="xl" thickness="normal" variant="default" />
        </SplitsDialogContent>
      ) : (
        <SplitsDialogContent
          id="walletConnectApproval"
          title="Connecting via WalletConnect"
          variant="confirmation"
          isLoading={isApprovingSession}
          confirmButton={{
            text: 'Approve',
            onClick: async () => {
              if (!smartAccount) throw new Error('Smart account not found')

              if (smartAccount.orgId !== activeOrg?.id) {
                toast.error('Wrong workspace selected', {
                  description:
                    "Select the smart account's workspace before approving this WalletConnect session",
                })
                return
              }
              await approveSession(
                EvmAddress.format(smartAccount.address),
                filteredSupportedChainIds,
                pendingProposal,
              )

              setIsOpen(false)
            },
            isDisabled,
            disabledReason: !smartAccount
              ? 'This smart account does not exist'
              : hasUnsupportedByTeamsChains
                ? 'This app requires networks that Splits does not support'
                : hasNotEnabledOnOrgChains
                  ? 'Enable the required networks in Settings to connect'
                  : undefined,
          }}
          cancelButton={{
            text: 'Cancel',
            onClick: async () => {
              await rejectSession(pendingProposal)
              setIsOpen(false)
            },
          }}
        >
          <WalletConnectConnectionContent
            metadata={pendingProposal.params.proposer.metadata}
            isDanger={isDanger}
            isVerified={isVerified}
            supportedChainIds={filteredSupportedChainIds}
            requiredChainIds={requiredChainIds}
            optionalChainIds={optionalChainIds}
          />
        </SplitsDialogContent>
      )}
    </Dialog>
  )
}

const WalletConnectConnectionContent = ({
  metadata,
  isDanger,
  isVerified,
  supportedChainIds,
  requiredChainIds,
  optionalChainIds,
}: {
  metadata: {
    name?: string
    icons: string[]
    url: string
    description?: string
  }
  isDanger: boolean
  isVerified: boolean
  supportedChainIds: number[]
  requiredChainIds: number[]
  optionalChainIds: number[]
}) => {
  const appIcon = metadata.icons[0]
  const appName = metadata.name ? metadata.name : undefined
  const appUrl = metadata.url
  const appDescription = metadata?.description ?? undefined

  const {
    formattedChainNames,
    isPlural,
    appSupportsCurrentChains,
    hasNoSupportedChains,
    unsupportedByTeamsChainNames,
    notEnabledOnOrgChainNames,
    hasUnsupportedByTeamsChains,
    hasNotEnabledOnOrgChains,
  } = useWalletConnectChainSupport({
    requiredChainIds,
    optionalChainIds,
    supportedChainIds,
  })

  return (
    <>
      <div className="flex space-x-4">
        <div>
          <WalletConnectAppIcon appIcon={appIcon} appName={appName} size={64} />
        </div>
        <div className="flex flex-1 flex-col space-y-2">
          <span className="line-clamp-1 font-medium leading-tight text-primary">
            {appName}
          </span>
          <span className="line-clamp-3 text-sm text-secondary">
            {appDescription}
          </span>
          <SplitsLink
            path={appUrl}
            target="tab"
            className="flex items-center gap-1.5 text-sm text-blue-500 hover:underline"
          >
            {appUrl} <ExternalLink className="size-3" />
          </SplitsLink>
        </div>
      </div>
      {appSupportsCurrentChains ? (
        <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-900 [&_div]:!ring-gray-50 [&_div]:dark:!ring-gray-900">
          <span className="pl-1 text-primary">Networks</span>
          <NetworkLogo size="lg" chainIds={supportedChainIds} limit={7} />
        </div>
      ) : (
        <Message variant="warning">
          <span>
            {hasUnsupportedByTeamsChains
              ? `This app requires ${unsupportedByTeamsChainNames.join(', ')} which Splits does not support.`
              : hasNotEnabledOnOrgChains
                ? `This app requires ${notEnabledOnOrgChainNames.join(', ')}. Enable ${
                    notEnabledOnOrgChainNames.length > 1
                      ? 'these networks'
                      : 'this network'
                  } in Settings to connect.`
                : hasNoSupportedChains
                  ? 'This app requires networks that are not supported by Splits.'
                  : `This app supports ${formattedChainNames}. Your account needs to have ${
                      isPlural
                        ? 'at least one of these networks'
                        : 'this network'
                    } enabled to connect.`}
          </span>
        </Message>
      )}
      {isVerified && (
        <Message variant="success">
          This app is verified by WalletConnect
        </Message>
      )}
      {isDanger && (
        <Message variant="danger">
          This app is flagged as suspicious by WalletConnect
        </Message>
      )}
    </>
  )
}

type ApproveWalletConnectTransactionFormValues = {
  memo: string
}

export const AcceptWalletConnectTransactionDialogContent = ({
  smartAccount,
  data,
  appName,
  appUrl,
  onClose,
  onAccept,
  onBatch,
  onError,
  warning,
  requestMethod,
}: {
  smartAccount: OrgSmartAccount
  data: CreateTransactionData
  appName?: string
  appUrl?: string
  onClose: () => void
  onAccept: (transaction: OrgTransaction) => void
  onBatch?: (operationId: string) => void
  onError: () => void
  warning?: string
  requestMethod?: string
}) => {
  const { control, handleSubmit } =
    useForm<ApproveWalletConnectTransactionFormValues>()
  const memo = useWatch({ control, name: 'memo' })
  const {
    approveAndSubmitTransaction,
    isProcessing: isProcessingApproveAndSubmit,
  } = useApproveAndSubmitTransaction()
  const { createTransaction, isProcessing: isCreatingTransaction } =
    useCreateTransaction()

  const isLoading = isCreatingTransaction || isProcessingApproveAndSubmit

  const acceptTransaction = useCallback(
    async (formData: ApproveWalletConnectTransactionFormValues) => {
      const transaction = await createTransaction({
        transactionId: undefined,
        txData: {
          ...data,
          memo: formData.memo,
        },
        dontOpenDialog: true,
      })

      if (!transaction) {
        onError()
        toast.error('Error creating transaction', {
          description: 'Please try again',
        })
        return
      }

      onAccept(transaction)

      return transaction
    },
    [createTransaction, data, onAccept, onError],
  )

  const onReview = handleSubmit(
    useCallback(
      async (formData: ApproveWalletConnectTransactionFormValues) => {
        await acceptTransaction(formData)
        onClose()
      },
      [acceptTransaction, onClose],
    ),
  )

  const onExecute = handleSubmit(
    useCallback(
      async (formData: ApproveWalletConnectTransactionFormValues) => {
        const transaction = await acceptTransaction(formData)
        if (!transaction) return
        approveAndSubmitTransaction({
          transaction,
          smartAccount,
        })
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [approveAndSubmitTransaction, acceptTransaction, smartAccount.address],
    ),
  )

  const canExecute = useCanExecuteNewTransaction(smartAccount)

  const appDomain = appUrl ? getDomainFromUrl(appUrl) : undefined

  const { contractCallSection, txnData, title } = useContractCallSection({
    smartAccountAddress: smartAccount.address,
    chainId: data.chainId,
    calls: data.calls,
    addressesToWatch: data.calls
      .map((call) => call.to)
      .concat(smartAccount.address)
      .filter(Boolean)
      .map((address) => EvmAddress.format(address)),
    source: appDomain,
  })

  const canAddToBatch = useCanAddToBatch({
    smartAccountId: smartAccount.id,
    chainId: data.chainId,
    requestMethod,
  })
  const { addCurrentFormToBatch, isProcessing: isAddingToBatch } =
    useAddCurrentFormToBatch({
      onSuccess: ({ addedOperation }) => {
        const operationId = addedOperation?.operationId
        if (operationId) onBatch?.(operationId)
      },
    })

  const handleAddToBatch = useCallback(async () => {
    await addCurrentFormToBatch({
      smartAccountId: smartAccount.id,
      chainId: data.chainId,
      title: title ?? data.name,
      types: [BasicTransactionType.WalletConnect],
      calls: data.calls,
      metadata: [],
      memo,
      walletConnectAppName: appName,
      walletConnectAppDomain: appDomain,
      setIsOpen: (open) => {
        if (!open) onClose()
      },
    })
  }, [
    addCurrentFormToBatch,
    smartAccount.id,
    data,
    title,
    memo,
    appName,
    appDomain,
    onClose,
  ])

  const addToBatchDropdownOptions = buildAddToBatchDropdownOptions({
    canAddToBatch,
    isDisabled: isLoading || isAddingToBatch,
    onClick: handleAddToBatch,
  })

  return (
    <SplitsDialogContent
      id="approveTransaction"
      title={title ?? data.name}
      variant="cta"
      isLoading={isLoading}
      actionButton={
        canExecute
          ? {
              // consolidated "Execute" and "Submit" button text to simplify UX
              text: 'Submit',
              onClick: onExecute,
              eventName: `clickedExecute`,
              eventProperties: {
                eventType: 'WalletConnectTransaction',
                walletConnectAppName: appName,
              },
              dropdownOptions: addToBatchDropdownOptions,
            }
          : {
              text: 'Create',
              onClick: onReview,
              dropdownOptions: addToBatchDropdownOptions,
            }
      }
    >
      <form className="flex flex-col items-start gap-4">
        {warning && <Message variant="warning">{warning}</Message>}
        {contractCallSection}
        <PendingStateChangeDisplay txnData={txnData} hideWhenEmpty />
        <TransactionDetailsPreview
          chainId={data.chainId}
          calls={data.calls}
          smartAccountId={data.smartAccountId}
          transactionTypes={[BasicTransactionType.WalletConnect]}
        />
        <TransactionMemoInput control={control} inputName={'memo'} />
      </form>
    </SplitsDialogContent>
  )
}

export const AcceptWalletConnectMessageDialogContent = ({
  smartAccount,
  data,
  icon,
  onClose,
  onAccept,
  onError,
}: {
  smartAccount: OrgSmartAccount
  data: CreateMessageData
  icon: React.ReactNode
  onClose: () => void
  onAccept: () => void
  onError: () => void
}) => {
  const { createMessage, isProcessing: isCreatingMessage } = useCreateMessage()
  const { approveMessage, isProcessing: isApprovingMessage } =
    useApproveMessage()

  const { respondWithSignature } = useWalletConnectContext()

  const isLoading = isCreatingMessage || isApprovingMessage

  const acceptMessage = useCallback(async () => {
    const message = await createMessage(data, true)

    if (!message) {
      onError()
      toast.error('Error creating message', {
        description: 'Please try again',
      })
      return
    }

    onAccept()

    return message
  }, [createMessage, data, onAccept, onError])

  const onReview = useCallback(async () => {
    await acceptMessage()
    onClose()
  }, [acceptMessage, onClose])

  const onExecute = useCallback(async () => {
    const message = await acceptMessage()
    if (!message) return

    approveMessage(message, false, (updatedMessage) => {
      respondWithSignature(updatedMessage)
      onClose()
    })
  }, [approveMessage, acceptMessage, onClose, respondWithSignature])

  const canExecute = useCanExecuteNewTransaction(smartAccount)

  return (
    <SplitsDialogContent
      id="approveMessage"
      title="New message"
      variant="cta"
      isLoading={isLoading}
      actionButton={
        canExecute
          ? {
              // consolidated "Execute" and "Submit" button text to simplify UX
              text: 'Submit',
              onClick: onExecute,
              isDisabled: false,
              fullWidth: true,
            }
          : {
              text: 'Create',
              onClick: onReview,
              isDisabled: false,
              fullWidth: true,
            }
      }
    >
      <div className="space-y-4">
        <MessageHeaderPreview name={data.name} icon={icon} />
        <MessageDetailsPreview
          chainId={data.chainId}
          smartAccountAddress={smartAccount.address}
          dataType={data.dataType}
          dataToSign={data.dataToSign}
        />
      </div>
    </SplitsDialogContent>
  )
}
